// Placas sin USB: Arduino Pro Mini (ATmega328P, SparkFun) y chip ATtiny85 suelto (DIP-8).
// Posiciones de la Pro Mini medidas del archivo de placa de SparkFun (Arduino-Pro-Mini.brd, v14): 17.78 × 33.02 mm,
// dos filas de 12 pines a 15.24 mm una de otra, encabezado FTDI de 6 pines en el extremo corto y las almohadillas
// A4–A7 sin encabezado. La placa queda con el extremo FTDI a la izquierda (en el archivo está arriba).
import { definePart, PITCH } from './registry.js';
import { dipPins } from './actuators.js';

// Arduino Pro Mini -------------------------------------------------------------------------------------
export const PROMINI = {
  W: 33.02,
  D: 17.78,
  ROW: 7.62, // las filas de pines están a ±7.62 mm del centro
  PCB_Y: 2.6, // la placa descansa sobre el separador de los pines de abajo, como el Nano
  PCB_T: 0.8,
};
PROMINI.PCB_TOP = PROMINI.PCB_Y + PROMINI.PCB_T;
PROMINI.HEADER_H = 8.5; // altura de las puntas del encabezado FTDI sobre la placa

const PWM = new Set(['3', '5', '6', '9', '10', '11']);
const proKind = (name) => {
  if (name === 'RAW' || name.startsWith('VCC')) return 'power';
  if (name.startsWith('GND')) return 'gnd';
  if (/^A\d$/.test(name)) return 'analog';
  if (PWM.has(name)) return 'pwm';
  return /^\d+$/.test(name) ? 'digital' : 'signal';
};
const proLabel = (name) => {
  const extra = { 0: 'RX · RXI', 1: 'TX · TXO', 2: 'INT0', 3: 'INT1', 10: 'SS', 11: 'MOSI', 12: 'MISO', 13: 'SCK · LED STAT' }[name];
  if (/^\d+$/.test(name)) return `D${name}${PWM.has(name) ? ' ~PWM' : ''}${extra ? ' · ' + extra : ''}`;
  if (name === 'RAW') return 'RAW · entrada del regulador (hasta 12 V)';
  if (name.startsWith('VCC')) return 'VCC · salida del regulador, o entrada directa de 3.3 V / 5 V';
  if (name.startsWith('GND')) return 'GND';
  if (name.startsWith('RESET')) return 'RST (reinicio)';
  if (name === 'A4') return 'A4 · SDA (almohadilla)';
  if (name === 'A5') return 'A5 · SCL (almohadilla)';
  if (name === 'A6' || name === 'A7') return `${name} (almohadilla, solo analógica)`;
  if (name === 'DTR') return 'DTR · GRN (reinicio automático al subir; no se simula)';
  if (name === 'TXO') return 'TXO · TX del Arduino (va al RXD del adaptador)';
  if (name === 'RXI') return 'RXI · RX del Arduino (viene del TXD del adaptador)';
  return name;
};

// Fila de arriba (de izquierda a derecha) y fila de abajo, con el extremo FTDI a la izquierda.
export const PROMINI_TOP = ['RAW', 'GND.1', 'RESET.1', 'VCC.1', 'A3', 'A2', 'A1', 'A0', '13', '12', '11', '10'];
export const PROMINI_BOTTOM = ['1', '0', 'RESET.2', 'GND.2', '2', '3', '4', '5', '6', '7', '8', '9'];
const proPins = [];
PROMINI_TOP.forEach((name, i) => proPins.push({ name, label: proLabel(name), kind: proKind(name), x: -12.7 + i * PITCH, y: 0, z: -PROMINI.ROW, insert: true }));
PROMINI_BOTTOM.forEach((name, i) => proPins.push({ name, label: proLabel(name), kind: proKind(name), x: -12.7 + i * PITCH, y: 0, z: PROMINI.ROW, insert: true }));
// Encabezado FTDI (de arriba abajo en la vista desde arriba): DTR (GRN), TXO, RXI, VCC, GND (CTS) y GND (BLK).
['DTR', 'TXO', 'RXI', 'VCC.2', 'GND.3', 'GND.4'].forEach((name, i) => {
  proPins.push({ name, label: proLabel(name), kind: proKind(name), x: -15.24, y: PROMINI.PCB_TOP + PROMINI.HEADER_H, z: (i - 2.5) * PITCH });
});
// Almohadillas sin encabezado: se sueldan cables o pines.
[['A4', -1.397], ['A5', -3.937], ['A6', 9.017], ['A7', 6.477]].forEach(([name, x]) => {
  proPins.push({ name, label: proLabel(name), kind: 'analog', x, y: PROMINI.PCB_TOP, z: -4.699 });
});

/**
 * Alimentación (client/sim/power.js). Esquemático de SparkFun (v14): RAW → MIC5205 de 5 V o 3.3 V (150 mA) → VCC, que
 * también es entrada directa (encabezado FTDI); en VCC, 10 µF + 2 × 0.1 µF y el LED de encendido (rojo, 10 kΩ). θJA del
 * MIC5205: 220 °C/W (su hoja de datos, cobre mínimo); capacidades térmicas estimadas para un SOT-23-5 (~15 mg) sobre
 * ~1 cm² de placa de 0.8 mm. ATmega328P con los fusibles de arduino:avr:pro (bajo 0xFF, extendido 0xFD): brown-out en
 * 2.7 V con 50 mV de histéresis, arranque de 16K ciclos + 65 ms. Frecuencia máxima contra VCC (hoja de datos): 4 MHz con
 * 1.8 V, 10 MHz con 2.7 V y 20 MHz con 4.5 V (rectas entre ellos): 8 MHz necesita 2.4 V y 16 MHz, 3.78 V. Consumo medido
 * por N. Gammon (ATmega328P sin dormir): 15.15 mA a 16 MHz y 5 V; 3.7 mA a 8 MHz y 3.3 V; más 0.3 mA del LED.
 */
function proMiniSupply(props) {
  const v3 = props.version === '3v3';
  const freq = v3 ? 8e6 : 16e6;
  return {
    rails: { VCC: { pins: ['VCC.1', 'VCC.2'], v: v3 ? 3.3 : 5, cap: 10.2e-6 } },
    mcu: { rail: 'VCC', chip: 'ATmega328P', bod: 2.7, hyst: 0.05, vmin: v3 ? 2.4 : 3.78, vmax: 5.5, vabs: 6, startup: 0.065 + 16384 / freq, load: v3 ? 0.0039 : 0.0155 },
    // SparkFun: RAW de 3.35 a 12 V (3.3 V) o de 5 a 12 V (5 V); el MIC5205 soporta 16 V.
    regulators: [{ part: v3 ? 'MIC5205-3.3' : 'MIC5205-5.0', from: { pin: 'RAW' }, to: 'VCC', rja: 220, cj: 0.01, cb: 0.15, range: v3 ? '3.35–12 V' : '5–12 V', vrec: 12 }],
    direct: v3 ? '3.3 V' : '5 V',
    led: { rail: 'VCC', r: 10000 },
  };
}

const AVR328_PINS = {
  0: 'PD0', 1: 'PD1', 2: 'PD2', 3: 'PD3', 4: 'PD4', 5: 'PD5', 6: 'PD6', 7: 'PD7',
  8: 'PB0', 9: 'PB1', 10: 'PB2', 11: 'PB3', 12: 'PB4', 13: 'PB5',
  A0: 'PC0', A1: 'PC1', A2: 'PC2', A3: 'PC3', A4: 'PC4', A5: 'PC5',
};

definePart({
  type: 'arduino-pro-mini',
  name: 'Arduino Pro Mini',
  category: 'placas',
  description: 'ATmega328P de 33 × 18 mm sin USB, para protoboard: versión de 5 V a 16 MHz o de 3.3 V a 8 MHz. Se alimenta por RAW (regulador de 150 mA) o por VCC, y su Serial llega al monitor con un adaptador USB-serie en el encabezado FTDI.',
  keywords: 'pro mini promini atmega328 328p sin usb ftdi pequeña 3.3v 8mhz',
  kind: 'board',
  insertable: true,
  props: [
    { key: 'version', label: 'Versión', type: 'select', default: '5v', options: [{ value: '5v', label: '5 V · 16 MHz' }, { value: '3v3', label: '3.3 V · 8 MHz' }] },
  ],
  variants: [
    { label: 'Arduino Pro Mini 5 V · 16 MHz', props: { version: '5v' } },
    { label: 'Arduino Pro Mini 3.3 V · 8 MHz', props: { version: '3v3' } },
  ],
  pins: proPins,
  buses: [['GND.1', 'GND.2', 'GND.3', 'GND.4'], ['RESET.1', 'RESET.2'], ['VCC.1', 'VCC.2'], ['1', 'TXO'], ['0', 'RXI']],
  footprint: { w: PROMINI.W + 2, d: PROMINI.D, h: 9 },
  help: 'No tiene USB: se alimenta por RAW (3.4–12 V en la de 3.3 V, 5–12 V en la de 5 V; el regulador MIC5205 da 150 mA) o por VCC con un voltaje ya regulado, ' +
    'por ejemplo el del adaptador USB-serie. Sin energía el MCU queda en reset. Para ver el monitor serie conecta un adaptador USB-serie: su TXD a RXI (D0) y su RXD a TXO (D1). ' +
    'I²C: A4 (SDA) y A5 (SCL) son almohadillas sin encabezado; A6 y A7 solo leen voltajes. La placa de 3.3 V trabaja a 8 MHz: no conectes 5 V a sus pines.',
  mcu: {
    chip: 'atmega328p',
    label: 'ATmega328P',
    usb: false,
    board: (props) => (props.version === '3v3' ? 'promini3' : 'promini5'),
    freq: (props) => (props.version === '3v3' ? 8e6 : 16e6),
    power: { 'GND.1': 0, 'GND.2': 0, 'GND.3': 0, 'GND.4': 0 },
    supply: proMiniSupply,
    pins: AVR328_PINS,
    adc: { A0: 0, A1: 1, A2: 2, A3: 3, A4: 4, A5: 5, A6: 6, A7: 7 },
    aref: null, // la Pro Mini no saca AREF
    reset: 'RESET.1',
    resetPullup: 10000,
    builtinLed: '13',
    usart: { tx: '1', rx: '0' },
    bus: { sda: 'A4', scl: 'A5', sck: '13', mosi: '11', miso: '12', ss: '10' },
  },
});

// ATtiny85 -----------------------------------------------------------------------------------------------
// DIP-8 (hoja de datos de Atmel): 1 PB5/RESET/ADC0, 2 PB3/ADC3, 3 PB4/ADC2/OC1B, 4 GND, 5 PB0/SDA/MOSI/OC0A/AREF,
// 6 PB1/MISO/OC0B/OC1A, 7 PB2/SCK/SCL/ADC1/INT0, 8 VCC. La numeración Arduino de ATTinyCore es 0–5 = PB0–PB5.
const TINY_PINS = ['PB5', 'PB3', 'PB4', 'GND', 'PB0', 'PB1', 'PB2', 'VCC'];
const TINY_LABELS = {
  PB5: 'PB5 · RESET · A0 (a GND reinicia el chip)',
  PB3: 'PB3 · D3 · A3',
  PB4: 'PB4 · D4 · A2 · PWM (OC1B)',
  PB0: 'PB0 · D0 · SDA · MOSI · PWM (OC0A) · AREF · TX de Serial',
  PB1: 'PB1 · D1 · MISO · PWM (OC0B, OC1A) · RX de Serial · LED_BUILTIN',
  PB2: 'PB2 · D2 · SCL · SCK · A1 · INT0',
  VCC: 'VCC (2.7–5.5 V)',
};
const TINY_KINDS = { PB5: 'signal', PB3: 'analog', PB4: 'pwm', PB0: 'pwm', PB1: 'pwm', PB2: 'analog' };

/**
 * Alimentación del chip suelto (hoja de datos Atmel 2586Q): sin regulador; en VCC solo su propia capacidad (se supone
 * el 0.1 µF que se pone junto al chip). ATTinyCore deja el brown-out apagado (BODLEVEL 111, su opción por omisión), así
 * que el chip solo queda en reset por el power-on reset: se activa al bajar de 1.3 V y lo suelta al subir de 1.4 V
 * (típicos, tabla 21-6). Velocidad: hasta 10 MHz con 2.7–5.5 V y 20 MHz con 4.5–5.5 V (recta entre ambos): 16 MHz
 * necesita 3.78 V. Arranque tras el reset: 14 ciclos + 64 ms (fusible bajo 0xE2, SUT = 10) y con el PLL (0xF1, SUT = 11)
 * 16K ciclos más. Consumo típico a 8 MHz y 5 V: 5 mA (tabla 21-1); a 16 MHz se supone el doble.
 */
function attinySupply(props) {
  const pll = props.clock === '16pll';
  return {
    rails: { VCC: { pins: ['VCC'], v: 5, cap: 0.1e-6 } },
    mcu: { rail: 'VCC', chip: 'ATtiny85', bod: 1.35, hyst: 0.1, por: true, vmin: pll ? 3.78 : 2.7, vmax: 5.5, vabs: 6, startup: pll ? 0.064 + 16384 / 16e6 : 0.064, load: pll ? 0.01 : 0.005 },
    direct: '2.7–5.5 V',
  };
}

definePart({
  type: 'attiny85',
  name: 'ATtiny85',
  category: 'placas',
  description: 'Microcontrolador AVR de 8 pines (8 KB de flash, 512 B de RAM y de EEPROM) para protoboard: 5 pines de E/S con PWM, ADC, I²C por USI y reloj interno de 8 MHz o PLL de 16 MHz. No tiene USB.',
  keywords: 'attiny85 attiny tiny85 dip8 usi tinywirem digispark attinycore',
  kind: 'board',
  insertable: true,
  props: [
    { key: 'clock', label: 'Reloj', type: 'select', default: '8internal', options: [{ value: '8internal', label: '8 MHz interno' }, { value: '16pll', label: '16 MHz (PLL)' }] },
  ],
  pins: dipPins(TINY_PINS, TINY_LABELS, TINY_KINDS),
  footprint: { w: 11, d: 9, h: 6 },
  help: 'Necesita alimentación: VCC (2.7–5.5 V) y GND, por ejemplo de unas pilas o del adaptador USB-serie; sin ella queda en reset. ' +
    'El pin 1 (PB5) es RESET: a GND reinicia el chip y no sirve como E/S (el fusible RSTDISBL no se simula), pero se puede leer con analogRead(A0). ' +
    'Serial (ATTinyCore) es por software: TX en PB0 y RX en PB1, que usa el comparador analógico; pásalo por un adaptador USB-serie para verlo en el monitor. ' +
    'I²C: Wire o TinyWireM por el USI, con SDA en PB0 y SCL en PB2; sirven las pull-up internas del chip, pero en un circuito real conviene poner resistencias de 4.7 kΩ. ' +
    'U8g2 y U8x8 no traen I²C de hardware para el ATtiny85: usa sus variantes SW_I2C (el I²C por software también funciona en cualquier otro par de pines). ' +
    'El SPI por el USI no lo atienden los módulos SPI «de hardware» (sí los que escuchan los pines, como los de shiftOut). ' +
    'Dormir (sleep) no detiene el reloj y el Timer1 con reloj PLL de 64 MHz no se simula.',
  mcu: {
    chip: 'attiny85',
    label: 'ATtiny85',
    usb: false,
    board: (props) => (props.clock === '16pll' ? 'attiny85pll' : 'attiny85'),
    freq: (props) => (props.clock === '16pll' ? 16e6 : 8e6),
    power: { GND: 0 },
    supply: attinySupply,
    pins: { PB0: 'PB0', PB1: 'PB1', PB2: 'PB2', PB3: 'PB3', PB4: 'PB4' }, // PB5 es RESET (RSTDISBL sin programar)
    adc: { PB5: 0, PB2: 1, PB4: 2, PB3: 3 },
    aref: 'PB0',
    reset: 'PB5',
    resetPullup: 45000,
    builtinLed: null,
    comparator: { ain0: 'PB0', ain1: 'PB1' },
    bus: { sda: 'PB0', scl: 'PB2' },
  },
});
